import React, { useState, useEffect, useRef } from 'react';
import { surveyBrands } from './surveyBrands';
import { surveySteps } from './surveySteps';
import { CUSTOM_BRAND_ID, DEFAULT_CUSTOM_CONFIG, fontValueFor, getContrastColor } from './surveyCustomize';
import BrandSwitcher from './BrandSwitcher';
import CustomizePanel from './CustomizePanel';
import RatingScale from './RatingScale';
import OpenEndedQuestion from './OpenEndedQuestion';
import NavigationBar from './NavigationBar';

const AUTO_ADVANCE_DELAY = 500; // Time to let the selection register before advancing

function SurveyDemo() {
  const [brandId, setBrandId] = useState(surveyBrands[0].id);
  const [customConfig, setCustomConfig] = useState(DEFAULT_CUSTOM_CONFIG);
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [showRequiredError, setShowRequiredError] = useState(false);
  const [cardHeight, setCardHeight] = useState(null);
  const autoAdvanceTimeoutRef = useRef(null);
  const cardRef = useRef(null);

  // The card's aspect-ratio-derived height is meant to govern the panel's
  // height too — but CSS align-items:stretch can't express "match THIS
  // sibling" when the other sibling's own content is taller (stretch just
  // takes the largest of the two, which fought the aspect-ratio entirely).
  // Measuring it directly and applying it as an explicit height sidesteps
  // that; a ResizeObserver catches every cause of the card resizing
  // (window resize, the panel opening/closing and changing its width).
  useEffect(() => {
    const node = cardRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      // offsetHeight (not contentRect, which excludes padding/border) so
      // this matches the card's actual full rendered box height.
      setCardHeight(node.offsetHeight);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const isCustom = brandId === CUSTOM_BRAND_ID;
  const brand = isCustom
    ? {
        id: CUSTOM_BRAND_ID,
        name: 'Custom',
        tagline: customConfig.tagline,
        wordmark: customConfig.wordmark,
        segmentedScale: customConfig.scaleStyle === 'segmented',
        autoAdvance: customConfig.autoAdvance,
        hideNavButtons: customConfig.autoAdvance && customConfig.hideNavButtons,
      }
    : surveyBrands.find((b) => b.id === brandId);

  const step = surveySteps[stepIndex];
  const isLastStep = stepIndex === surveySteps.length - 1;
  const isAutoAdvanceStep = brand.autoAdvance && step.type !== 'open';

  useEffect(() => {
    return () => {
      if (autoAdvanceTimeoutRef.current) {
        clearTimeout(autoAdvanceTimeoutRef.current);
      }
    };
  }, []);

  // Auto-advance calls this directly (bypassing the required check below) —
  // it only ever fires right after setAnswer just recorded a value, so
  // re-validating would just be checking a still-stale `answers` closure
  // from before that state update landed.
  const advance = () => {
    if (isLastStep) {
      setSubmitted(true);
    } else {
      setStepIndex((i) => i + 1);
    }
  };

  const handleNext = () => {
    const answer = answers[step.id];
    const isAnswered = answer !== undefined && answer !== '';
    if (!isAnswered) {
      setShowRequiredError(true);
      return;
    }
    setShowRequiredError(false);
    advance();
  };

  const setAnswer = (value) => {
    setAnswers((prev) => ({ ...prev, [step.id]: value }));
    setShowRequiredError(false);

    if (isAutoAdvanceStep) {
      if (autoAdvanceTimeoutRef.current) {
        clearTimeout(autoAdvanceTimeoutRef.current);
      }
      autoAdvanceTimeoutRef.current = setTimeout(advance, AUTO_ADVANCE_DELAY);
    }
  };

  const handleBack = () => {
    setShowRequiredError(false);
    setStepIndex((i) => Math.max(0, i - 1));
  };

  const handleRestart = () => {
    setStepIndex(0);
    setAnswers({});
    setSubmitted(false);
    setShowRequiredError(false);
  };

  const resetSurveyProgress = () => {
    if (autoAdvanceTimeoutRef.current) {
      clearTimeout(autoAdvanceTimeoutRef.current);
    }
    setStepIndex(0);
    setAnswers({});
    setSubmitted(false);
    setShowRequiredError(false);
  };

  const handleBrandChange = (newBrandId) => {
    resetSurveyProgress();
    setBrandId(newBrandId);
    setIsCustomizeOpen(newBrandId === CUSTOM_BRAND_ID);
  };

  const handleCustomConfigChange = (partial) => {
    setCustomConfig((prev) => ({ ...prev, ...partial }));
  };

  const customStyle = isCustom
    ? {
        '--survey-color-primary': customConfig.colors.primary,
        '--survey-color-success': customConfig.colors.success,
        '--survey-color-info': customConfig.colors.info,
        '--survey-color-danger': customConfig.colors.danger,
        '--survey-color-text': customConfig.colors.text,
        '--survey-color-accent': customConfig.colors.page,
        '--survey-color-on-primary': getContrastColor(customConfig.colors.primary),
        '--survey-font-heading': fontValueFor(customConfig.font),
        '--survey-font-body': fontValueFor(customConfig.font),
        '--survey-button-bg': customConfig.colors.primary,
        '--survey-button-color': getContrastColor(customConfig.colors.primary),
        '--survey-button-border': '2px solid transparent',
        // A dark page needs a dark (not light) textarea overlay to stay
        // readable against its light text — getContrastColor already tells
        // us which way a background leans, so reuse it rather than a
        // second luminance check.
        '--survey-textarea-bg': getContrastColor(customConfig.colors.page) === '#ffffff'
          ? 'rgba(0, 0, 0, 0.3)'
          : 'rgba(255, 255, 255, 0.5)',
      }
    : undefined;

  return (
    <div id="surveyDemo" className="survey-theme-demo" data-brand={brandId} style={customStyle}>
      <div className="survey-demo-controls-row">
        <BrandSwitcher brands={surveyBrands} selectedBrandId={brandId} onChange={handleBrandChange} />
        {isCustom && !isCustomizeOpen && (
          <button
            type="button"
            className="survey-customize-reopen"
            onClick={() => setIsCustomizeOpen(true)}
          >
            Edit customization
          </button>
        )}
      </div>

      <div className="survey-demo-layout">
        {isCustom && (
          <CustomizePanel
            isOpen={isCustomizeOpen}
            onClose={() => setIsCustomizeOpen(false)}
            config={customConfig}
            onChange={handleCustomConfigChange}
            matchHeight={cardHeight}
          />
        )}

        <div className="survey-card" ref={cardRef}>
          <div className="survey-logo">{brand.wordmark}</div>
          <p className="survey-tagline">{brand.tagline}</p>

          {!submitted ? (
            <>
              <div className="survey-question" key={step.id}>
                <p className="survey-prompt">{step.prompt}</p>
                {step.type === 'open' ? (
                  <OpenEndedQuestion
                    value={answers[step.id] || ''}
                    onChange={setAnswer}
                  />
                ) : (
                  <RatingScale
                    scaleType={step.type}
                    value={answers[step.id]}
                    onChange={setAnswer}
                    min={step.min}
                    max={step.max}
                    minLabel={step.minLabel}
                    maxLabel={step.maxLabel}
                    segmented={brand.segmentedScale}
                  />
                )}
                {showRequiredError && (
                  <p className="survey-error">This question is required</p>
                )}
              </div>
              <NavigationBar
                currentStep={stepIndex + 1}
                totalSteps={surveySteps.length}
                onBack={handleBack}
                onNext={handleNext}
                backDisabled={stepIndex === 0}
                nextLabel={isLastStep ? 'Submit' : 'Next'}
                hideBack={brand.autoAdvance && brand.hideNavButtons}
                hideNext={isAutoAdvanceStep && brand.hideNavButtons}
              />
            </>
          ) : (
            <div className="survey-complete">
              <h3>Thanks for your feedback!</h3>
              <p>This is a demo — no data was submitted anywhere.</p>
              <button type="button" className="survey-nav-back" onClick={handleRestart}>
                Start Over
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SurveyDemo;
